import React, { useState, useEffect } from "react";
import { Building2, Sparkles, Pencil, AlertTriangle, Network } from "lucide-react";
import DeliverableBar from "./DeliverableBar";
import ValueStreamHeatStrip from "./ValueStreamHeatStrip";
import { BASE, Eyebrow, bandColor, dispColor } from "./e360Primitives";

export default function BlueprintView({ bp, onEditMap, kitClientId, showKit = true, streamCounts = {}, streamInsights = {}, qaProfile = null }) {
  const cov = bp.ekselens360_coverage;
  const [ackLowConf, setAckLowConf] = useState(false);
  const lowConf = bp.match_confidence && bp.match_confidence.level !== "matched";
  const [qa, setQa] = useState(null);
  const [qaLoading, setQaLoading] = useState(false);
  const [enrich, setEnrich] = useState(null);
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [enrichErr, setEnrichErr] = useState(false);
  const [exportQa, setExportQa] = useState(null);
  const loadExportQa = React.useCallback(async () => {
    if (!kitClientId || !showKit) { setExportQa(null); return; }
    try {
      const res = await fetch(`${BASE}/api/admin/enterprise360/clients/${kitClientId}/qa`, { credentials: "include" });
      if (res.ok) setExportQa(await res.json());
    } catch { /* best-effort */ }
  }, [kitClientId, showKit]);
  useEffect(() => { loadExportQa(); }, [loadExportQa]);
  const acknowledgeQa = async (checkId) => {
    const note = window.prompt("Add a short justification for proceeding with this known issue (optional):", "") ?? "";
    try {
      const res = await fetch(`${BASE}/api/admin/enterprise360/clients/${kitClientId}/qa/acknowledge`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ check_id: checkId, note }),
      });
      if (res.ok) loadExportQa();
    } catch { /* best-effort */ }
  };
  const [layoutQa, setLayoutQa] = useState(null);
  const [layoutLoading, setLayoutLoading] = useState(false);
  const [layoutErr, setLayoutErr] = useState(false);
  const runLayoutQa = async () => {
    if (!kitClientId) return;
    setLayoutLoading(true); setLayoutErr(false); setLayoutQa(null);
    try {
      const res = await fetch(`${BASE}/api/admin/enterprise360/clients/${kitClientId}/layout-qa`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      setLayoutQa(await res.json());
    } catch { setLayoutErr(true); }
    setLayoutLoading(false);
  };
  useEffect(() => {
    if (!kitClientId || !showKit) { setEnrich(null); return; }
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${BASE}/api/admin/enterprise360/clients/${kitClientId}/enrich-measures`, { credentials: "include" });
        if (alive && res.ok) setEnrich(await res.json());
      } catch { /* status is best-effort */ }
    })();
    return () => { alive = false; };
  }, [kitClientId, showKit]);
  const enrichMeasures = async () => {
    if (!kitClientId) return;
    setEnrichLoading(true); setEnrichErr(false);
    try {
      const res = await fetch(`${BASE}/api/admin/enterprise360/clients/${kitClientId}/enrich-measures`, {
        method: "POST", credentials: "include",
      });
      if (!res.ok) throw new Error("failed");
      setEnrich(await res.json());
    } catch { setEnrichErr(true); }
    setEnrichLoading(false);
  };
  const runNarrativeQa = async () => {
    if (!qaProfile) return;
    setQaLoading(true); setQa(null);
    try {
      const res = await fetch(`${BASE}/api/admin/enterprise360/narrative-qa`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(qaProfile),
      });
      setQa(await res.json());
    } catch { setQa({ error: true }); }
    setQaLoading(false);
  };
  const [mmLoading, setMmLoading] = useState(false);
  const exploreMindMap = async (view = "maturity") => {
    if (!kitClientId) return;
    setMmLoading(true);
    try {
      const q = view === "evidence" ? "?view=evidence" : "";
      const res = await fetch(`${BASE}/api/admin/enterprise360/clients/${kitClientId}/mindmap.html${q}`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank", "noopener");
    } catch (e) {
      window.alert(`Could not open the mind map: ${e.message || e}`);
    }
    setMmLoading(false);
  };
  const sevColor = (s) => s === "high" ? "text-red-700 bg-red-50 border-red-200"
    : s === "medium" ? "text-amber-700 bg-amber-50 border-amber-200"
    : "ek-text-muted dark:text-[var(--ek-dark-text-muted)] ek-surface-2 dark:bg-[var(--ek-dark-surface-hi)] ek-line dark:border-[var(--ek-dark-border)]";
  const kitGroups = [
    {
      id: "enterprise360", label: "ENTERPRISE360 deliverables",
      items: [
        { id: "kit", label: "Reinvention Kit (ZIP)", note: "Single narrated report with a table of contents + editable one-pagers, editable BPMN maps AND view-only process PDFs (no software needed). Edit maps free at diagrams.net.",
          url: `${BASE}/api/admin/enterprise360/clients/${kitClientId}/kit` },
        { id: "cross-sell-brief", label: "Maturity & Next-Steps Brief (PDF)",
          note: "Client-facing leave-behind from the Maturity Spine — co-branded, doubles as a proposal opener",
          url: `${BASE}/api/admin/enterprise360/clients/${kitClientId}/cross-sell-brief.pdf` },
        { id: "board-deck", label: "Board Briefing Deck (PPTX)",
          note: "One-click sponsor deck — archetype, heatmap, value-at-stake, trend & next moves. Co-branded & editable.",
          url: `${BASE}/api/admin/enterprise360/clients/${kitClientId}/board-deck.pptx` },
        { id: "mindmap", label: "Reinvention Mind Map (interactive HTML)",
          note: "Self-contained, offline, click-to-explore map of the whole reinvention — collapse/expand branches, print the whole map or one section. Also inside the Kit ZIP.",
          url: `${BASE}/api/admin/enterprise360/clients/${kitClientId}/mindmap.html` },
        { id: "mindmap-evidence", label: "Mind Map — evidence-tier view (interactive HTML)",
          note: "The same map coloured by evidence strength — shows at a glance which streams are backed by a scored diagnostic vs still self-declared. Also inside the Kit ZIP.",
          url: `${BASE}/api/admin/enterprise360/clients/${kitClientId}/mindmap.html?view=evidence` },
      ],
    },
  ];
  return (
    <div data-testid="e360-blueprint" className="space-y-6">
      <ValueStreamHeatStrip bp={bp} />
      <DeliverableBar
        testId="e360-deliverable-bar"
        label="ENTERPRISE360 deliverables"
        description="Generate the Reinvention Kit, the client-facing brief and the board deck — fully branded, with editable process maps. Pick what you need and generate in one place."
        studioTitle="ENTERPRISE360 deliverables"
        studioSubtitle="Tick what you want, generate, and ready items are highlighted with a re-download."
        groups={kitClientId && showKit ? kitGroups : []}
        disabled={!kitClientId || !showKit || (lowConf && !ackLowConf) || (exportQa && exportQa.blocked)}
        disabledHint={
          !kitClientId ? "Save this profile as a client to generate the Reinvention Kit, brief & board deck."
            : !showKit ? "Reinvention Kit export isn't enabled on this account — ask your admin to enable kit export."
            : (exportQa && exportQa.blocked) ? "Export blocked by the pre-export QA gate — resolve the blockers below before generating."
            : undefined
        }
        notice={kitClientId && showKit && lowConf ? (
          <label data-testid="e360-export-ack" className="flex items-start gap-2 rounded-md bg-amber-500/10 border border-amber-400/50 px-3 py-2 cursor-pointer">
            <input type="checkbox" checked={ackLowConf} onChange={(e) => setAckLowConf(e.target.checked)}
              className="mt-0.5" data-testid="e360-export-ack-checkbox" />
            <span className="text-[12px] text-amber-900/90 dark:text-amber-200 leading-relaxed">
              This blueprint uses a <strong>generic (low-confidence) recipe</strong>. I understand the deliverables may not be fully tailored and want to export anyway. Refine the profile for a sharper, sector-specific Kit.
            </span>
          </label>
        ) : null}
      />
      {kitClientId && showKit && (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => exploreMindMap("maturity")} disabled={mmLoading}
            data-testid="e360-explore-mindmap"
            className="inline-flex items-center gap-2 text-[13px] font-medium text-[#7737F6] border border-[#7737F6]/40 hover:bg-[#7737F6]/5 px-4 py-2 rounded-md disabled:opacity-50">
            <Network size={15} /> {mmLoading ? "Opening…" : "Explore reinvention mind map"}
          </button>
          <button type="button" onClick={() => exploreMindMap("evidence")} disabled={mmLoading}
            data-testid="e360-explore-mindmap-evidence"
            className="inline-flex items-center gap-2 text-[13px] font-medium text-[#7737F6] border border-[#7737F6]/40 hover:bg-[#7737F6]/5 px-4 py-2 rounded-md disabled:opacity-50">
            <Network size={15} /> {mmLoading ? "Opening…" : "Evidence-tier view"}
          </button>
        </div>
      )}
      {kitClientId && showKit && exportQa && (
        <div data-testid="e360-export-qa" className={`rounded-lg border p-4 ${exportQa.status === "fail" ? "border-red-200 bg-red-50" : exportQa.status === "warn" ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[12px] tracking-[0.14em] uppercase text-[#7737F6] font-semibold">Pre-export QA gate</div>
              <div className="text-[12px] ek-text-muted dark:text-[var(--ek-dark-text-muted)] mt-0.5">Data, roadmap-horizon & Transformation-Accelerator checks. A hard fail blocks export.</div>
            </div>
            <span data-testid="e360-export-qa-status" className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${exportQa.status === "fail" ? "bg-red-600 text-white" : exportQa.status === "warn" ? "bg-amber-500 text-amber-950" : "bg-emerald-600 text-white"}`}>
              {exportQa.status === "fail" ? `${exportQa.summary.hard_fails} blocker${exportQa.summary.hard_fails === 1 ? "" : "s"} — export blocked`
                : exportQa.status === "warn" ? `${exportQa.summary.warnings} warning${exportQa.summary.warnings === 1 ? "" : "s"}`
                : `All ${exportQa.summary.passed} checks passed ✓`}
            </span>
          </div>
          {(exportQa.status !== "pass") && (
            <ul className="mt-3 space-y-1.5">
              {exportQa.checks.filter((c) => !c.ok).map((c) => (
                <li key={c.id} className="flex items-start gap-2 text-[12.5px]" data-testid={`e360-qa-issue-${c.id}`}>
                  <AlertTriangle size={14} className={`mt-0.5 flex-none ${c.severity === "error" ? "text-red-600" : "text-amber-500"}`} />
                  <span className={c.severity === "error" ? "text-red-800 dark:text-red-300" : "text-amber-800 dark:text-amber-300"}>
                    <strong>{c.label}</strong> — {c.detail}
                    {c.acknowledged ? (
                      <span className="ml-1 text-[11px] ek-text-muted dark:text-[var(--ek-dark-text-muted)] italic" data-testid={`e360-qa-ack-${c.id}`}>
                        · Acknowledged by {c.acknowledged_by}{c.acknowledged_at ? ` on ${new Date(c.acknowledged_at).toLocaleDateString("en-GB")}` : ""}{c.severity === "error" ? " (export still blocked until fixed)" : ""}
                      </span>
                    ) : (
                      <button type="button" onClick={() => acknowledgeQa(c.id)} className="ml-2 text-[11px] font-medium text-[#7737F6] underline" data-testid={`e360-qa-ack-btn-${c.id}`}>
                        Acknowledge
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {kitClientId && showKit && (
        <div data-testid="e360-layout-qa" className="rounded-lg border ek-line dark:border-[var(--ek-dark-border)] p-4 ek-surface dark:bg-[var(--ek-dark-surface)]">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[12px] tracking-[0.14em] uppercase text-[#7737F6] font-semibold">Rendered-layout QA</div>
              <div className="text-[12px] ek-text-muted dark:text-[var(--ek-dark-text-muted)] mt-0.5">Renders every Kit PDF and scans the geometry for text-over-text overlaps and content clipped off the page edge — catch a broken export before you issue it. Takes a few seconds.</div>
              {layoutQa ? (
                <div data-testid="e360-layout-qa-status" className="text-[11px] mt-1.5">
                  <span className={layoutQa.status === "pass" ? "text-emerald-700 font-medium" : "text-red-700 font-medium"}>
                    {layoutQa.status === "pass"
                      ? `✓ All ${layoutQa.summary.documents} documents render cleanly`
                      : `${layoutQa.defects} layout defect${layoutQa.defects === 1 ? "" : "s"} across ${layoutQa.summary.with_defects} document${layoutQa.summary.with_defects === 1 ? "" : "s"}`}
                  </span>
                </div>
              ) : exportQa?.last_layout_qa ? (
                <div data-testid="e360-layout-qa-last" className="text-[11px] mt-1.5">
                  <span className={exportQa.last_layout_qa.status === "pass" ? "text-emerald-700 font-medium" : "text-red-700 font-medium"}>
                    {exportQa.last_layout_qa.status === "pass"
                      ? `✓ Last download was layout-checked`
                      : `⚠ ${exportQa.last_layout_qa.defects} layout defect${exportQa.last_layout_qa.defects === 1 ? "" : "s"} on last download`}
                  </span>
                  {exportQa.last_layout_qa.at && (
                    <span className="ek-text-muted dark:text-[var(--ek-dark-text-muted)]"> · {new Date(exportQa.last_layout_qa.at).toLocaleDateString("en-GB")}</span>
                  )}
                </div>
              ) : null}
              {layoutErr && <div className="text-[11px] text-red-600 mt-1.5">Layout QA failed to run — please retry.</div>}
            </div>
            <button type="button" onClick={runLayoutQa} disabled={layoutLoading}
              data-testid="e360-layout-qa-btn"
              className="inline-flex items-center gap-2 border border-[#7737F6] text-[#7737F6] px-4 py-2 text-[11px] tracking-[0.14em] uppercase rounded hover:bg-[#7737F6]/5 transition-colors disabled:opacity-40">
              <AlertTriangle size={14} /> {layoutLoading ? "Scanning…" : "Run layout QA"}
            </button>
          </div>
          {layoutQa && layoutQa.status !== "pass" && (
            <ul className="mt-3 space-y-1.5" data-testid="e360-layout-qa-defects">
              {layoutQa.artifacts.filter((a) => !a.ok).map((a, i) => (
                <li key={i} className="text-[12.5px] text-red-800" data-testid={`e360-layout-defect-${i}`}>
                  <strong>{a.name}</strong>
                  {a.error ? ` — render error: ${a.error}` : (
                    <>
                      {a.overlaps?.length > 0 && ` — overlapping text on page(s) ${a.overlaps.map((o) => o.page).join(", ")}`}
                      {a.clipped?.length > 0 && ` — content clipped off page(s) ${a.clipped.map((c) => c.page).join(", ")}`}
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {kitClientId && showKit && (
        <div data-testid="e360-enrich-measures" className="rounded-lg border ek-line dark:border-[var(--ek-dark-border)] p-4 ek-surface dark:bg-[var(--ek-dark-surface)]">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[12px] tracking-[0.14em] uppercase text-[#7737F6] font-semibold">AI narrative enrichment</div>
              <div className="text-[12px] ek-text-muted dark:text-[var(--ek-dark-text-muted)] mt-0.5">Tailors the "what we measure &amp; why" panel per value stream (sector- &amp; stream-specific). Cached — the Kit stays fast. Falls back to the standard text if unavailable.</div>
              {enrich?.enriched && (
                <div data-testid="e360-enrich-status" className="text-[11px] mt-1.5">
                  <span className="text-emerald-700 font-medium">✓ Enriched — {enrich.count} stream{enrich.count === 1 ? "" : "s"}</span>
                  {enrich.generated_at && <span className="ek-text-muted dark:text-[var(--ek-dark-text-muted)]"> · {new Date(enrich.generated_at).toLocaleDateString()}</span>}
                  {enrich.stale && <span className="ml-1 text-amber-600 font-medium">· blueprint changed — regenerate</span>}
                </div>
              )}
              {enrichErr && <div className="text-[11px] text-red-600 mt-1.5">AI enrichment failed — please retry.</div>}
            </div>
            <button type="button" onClick={enrichMeasures} disabled={enrichLoading}
              data-testid="e360-enrich-measures-btn"
              className="inline-flex items-center gap-2 border border-[#7737F6] text-[#7737F6] px-4 py-2 text-[11px] tracking-[0.14em] uppercase rounded hover:bg-[#7737F6]/5 transition-colors disabled:opacity-40">
              <Sparkles size={14} /> {enrichLoading ? "Enriching…" : enrich?.enriched ? "Regenerate narrative" : "Enrich narrative (AI)"}
            </button>
          </div>
        </div>
      )}
      {qaProfile && (
        <div data-testid="e360-narrative-qa" className="rounded-lg border ek-line dark:border-[var(--ek-dark-border)] p-4 ek-surface dark:bg-[var(--ek-dark-surface)]">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[12px] tracking-[0.14em] uppercase text-[#7737F6] font-semibold">Narrative quality QA</div>
              <div className="text-[12px] ek-text-muted dark:text-[var(--ek-dark-text-muted)] mt-0.5">AI senior-reviewer pass over the tailored narrative — flags generic prose & sector mismatches a structural check can't see.</div>
            </div>
            <button type="button" onClick={runNarrativeQa} disabled={qaLoading}
              data-testid="e360-run-narrative-qa"
              className="inline-flex items-center gap-2 border border-[#7737F6] text-[#7737F6] px-4 py-2 text-[11px] tracking-[0.14em] uppercase rounded hover:bg-[#7737F6]/5 transition-colors disabled:opacity-40">
              <Sparkles size={14} /> {qaLoading ? "Reviewing…" : "Run narrative QA"}
            </button>
          </div>
          {qa && !qa.error && (
            <div className="mt-3" data-testid="e360-narrative-qa-result">
              <div className="flex items-center gap-2 text-[13px]">
                <span className={`px-2 py-0.5 rounded-full border text-[11px] font-semibold uppercase ${qa.verdict === "pass" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : qa.verdict === "fail" ? "text-red-700 bg-red-50 border-red-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>
                  {qa.verdict}
                </span>
                <span className="ek-text-muted dark:text-[var(--ek-dark-text-muted)]">{qa.summary}</span>
                {qa.ai_available === false && <span className="text-[11px] ek-text-muted dark:text-[var(--ek-dark-text-muted)]">(checklist only — AI unavailable)</span>}
              </div>
              {(qa.findings || []).length > 0 && (
                <ul className="mt-2 space-y-1.5" data-testid="e360-narrative-qa-findings">
                  {qa.findings.map((f, i) => (
                    <li key={i} className={`text-[12px] rounded border px-2.5 py-1.5 ${sevColor(f.severity)}`}>
                      <span className="font-semibold">{f.stream}</span> · {f.issue}
                      {f.suggestion && <span className="block text-[11px] opacity-80 mt-0.5">Fix: {f.suggestion}</span>}
                    </li>
                  ))}
                </ul>
              )}
              {(qa.checklist || []).length > 0 && (
                <div className="mt-2 text-[11px] ek-text-muted dark:text-[var(--ek-dark-text-muted)]">
                  {qa.checklist.map((c, i) => (
                    <div key={i}>{c.status === "fail" ? "✗" : c.status === "flag" ? "⚠" : "✓"} {c.stream !== "—" ? `${c.stream}: ` : ""}{c.note}</div>
                  ))}
                </div>
              )}
            </div>
          )}
          {qa && qa.error && <div className="mt-2 text-[12px] text-red-600">Could not run narrative QA — try again.</div>}
        </div>
      )}
      {/* Archetype */}
      <div className="bg-[#0B0B0C] text-white rounded-lg p-5">
        <div className="text-[11px] tracking-[0.18em] uppercase text-[#b89bff] mb-1"><Building2 size={13} className="inline mr-1" /> Business archetype</div>
        <div className="text-xl font-semibold">{bp.archetype?.name}</div>
        <div className="text-sm ek-text-muted dark:text-[var(--ek-dark-text-muted)] mt-1">{bp.archetype?.description}</div>
        {bp.match_confidence && bp.match_confidence.level !== "matched" && (
          <div className="mt-3 rounded-md bg-amber-500/15 border border-amber-400/50 px-3 py-2" data-testid="e360-match-warning">
            <div className="text-[12px] font-semibold text-amber-200 flex items-center gap-1.5">
              <AlertTriangle size={13} /> Low-confidence archetype match — generic recipe in use
            </div>
            <div className="text-[11.5px] text-amber-100/90 mt-1 leading-relaxed">{bp.match_confidence.warning}</div>
            {(bp.match_confidence.streams_not_in_recipe || []).length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="e360-missing-streams">
                {bp.match_confidence.streams_not_in_recipe.map((s) => (
                  <span key={s} className="text-[10.5px] bg-amber-400/20 text-amber-100 border border-amber-300/40 rounded-full px-2 py-0.5">
                    missing: {s.replace(/_/g, " ").replace(/^_/, "")}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        {(bp.archetype?.secondary_lines || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="e360-secondary-lines">
            {bp.archetype.secondary_lines.map((s) => (
              <span key={s.stream} title={s.note}
                className="text-[11px] bg-[#b89bff]/15 text-[#d7c4ff] border border-[#b89bff]/40 rounded-full px-3 py-1">
                + {s.label} line
              </span>
            ))}
          </div>
        )}
        <div className="text-xs ek-text-muted dark:text-[var(--ek-dark-text-muted)] mt-3">{bp.profile_summary}</div>
        {bp.operations_summary && (
          <div className="text-xs ek-text-muted dark:text-[var(--ek-dark-text-muted)] mt-2 border-t border-white/10 pt-2" data-testid="e360-operations-summary">
            <span className="ek-text-muted dark:text-[var(--ek-dark-text-muted)] uppercase tracking-wider text-[10px]">Operations footprint</span>
            <p className="mt-1 leading-relaxed">{bp.operations_summary}</p>
          </div>
        )}
      </div>

      {/* Overlays */}
      <div>
        <Eyebrow>Applied overlays</Eyebrow>
        <div className="flex flex-wrap gap-2">
          {(bp.applied_overlays || []).map((o) => (
            <span key={o.id} className="text-xs bg-violet-50 text-[var(--e360-primary)] border border-violet-200 rounded-full px-3 py-1">{o.display}</span>
          ))}
          {bp.sector_overlay?.display && <span className="text-xs ek-surface-2 dark:bg-[var(--ek-dark-surface-hi)] ek-text-muted dark:text-[var(--ek-dark-text-muted)] rounded-full px-3 py-1">Sector: {bp.sector_overlay.display}</span>}
        </div>
      </div>

      {/* Capability heatmap */}
      <div>
        <Eyebrow>Capability heatmap (current state)</Eyebrow>
        <p className="text-[11px] ek-text-muted dark:text-[var(--ek-dark-text-muted)] mb-2">Scored on one 0–100 maturity index, expressed in four shared bands.</p>
        <div className="border ek-line dark:border-[var(--ek-dark-border)] rounded-lg divide-y">
          {(bp.capability_heatmap || []).map((r) => (
            <div key={r.stream} className="flex items-center justify-between px-3 py-2">
              <span className="text-sm ek-text dark:text-[var(--ek-dark-text)]">{r.stream.replace(/_/g, " ")}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs ek-text-muted dark:text-[var(--ek-dark-text-muted)]">
                  {r.index != null ? `${r.index}/100` : "—"}
                  {r.source === "assessment" ? ` · from ${(r.from || []).join(", ")}` : " · indicative"}
                </span>
                <span className={`text-[11px] rounded-full px-2 py-0.5 ${bandColor(r.band)}`}>{r.band}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* EKSELENS360 coverage + white-space upsell */}
      {cov && cov.total_areas && (
        <div>
          <Eyebrow>EKSELENS360 coverage &amp; white-space (upsell)</Eyebrow>
          <div className="text-sm ek-text-muted dark:text-[var(--ek-dark-text-muted)] mb-2">
            {cov.assessed_count}/{cov.total_areas} areas assessed ({cov.coverage_pct}%)
            {cov.white_space_value_total_label && (
              <span className="ml-2 text-[var(--e360-primary)] font-medium">· ~{cov.white_space_value_total_label} indicative white-space opportunity</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {(cov.assessed || []).map((a) => <span key={a.sub_brand} className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-1">{a.area}</span>)}
            {(cov.white_space || []).map((w) => (
              <span key={w.sub_brand} className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-3 py-1" title={w.note}>
                {w.area}{w.indicative_label ? ` · ~${w.indicative_label}` : " (white space)"}
              </span>
            ))}
          </div>
          {cov.value_basis && <p className="text-[11px] ek-text-muted dark:text-[var(--ek-dark-text-muted)] mt-2">{cov.value_basis}</p>}
        </div>
      )}

      {/* Technology disposition */}
      {bp.technology_disposition?.systems?.length > 0 && (
        <div>
          <Eyebrow>Incumbent-system disposition (TIME)</Eyebrow>
          <div className="border ek-line dark:border-[var(--ek-dark-border)] rounded-lg divide-y">
            {bp.technology_disposition.systems.map((s) => (
              <div key={s.name} className="flex items-center justify-between px-3 py-2">
                <span className="text-sm ek-text dark:text-[var(--ek-dark-text)]">{s.name} <span className="text-xs ek-text-muted dark:text-[var(--ek-dark-text-muted)]">{s.category || ""}</span></span>
                <span className={`text-[11px] rounded-full px-2 py-0.5 ${dispColor(s.disposition)}`}>{s.disposition}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Function blueprints */}
      <div>
        <Eyebrow>Tailored function blueprints ({(bp.function_blueprints || []).length})</Eyebrow>
        <div className="space-y-3">
          {(bp.function_blueprints || []).map((fb) => (
            <details key={fb.id} className="border ek-line dark:border-[var(--ek-dark-border)] rounded-lg p-3" data-testid={`e360-fb-${fb.id}`}>
              <summary className="cursor-pointer font-medium ek-text dark:text-[var(--ek-dark-text)]">{fb.name} <span className="text-xs text-[var(--e360-primary)]">· {fb.applied_variant}</span>
                {streamCounts?.[fb.id] > 0 && (
                  <span data-testid={`e360-learnings-badge-${fb.id}`} title="Improvements published from past engagements"
                    className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wider bg-violet-50 text-[var(--e360-primary)] border border-violet-200 rounded-full px-2 py-0.5">
                    ↑ {streamCounts[fb.id]} learning{streamCounts[fb.id] > 1 ? "s" : ""} published
                  </span>
                )}
              </summary>
              <p className="text-sm ek-text-muted dark:text-[var(--ek-dark-text-muted)] mt-2">{fb.what_good_looks_like_resolved}</p>
              {(streamInsights?.[fb.id] || []).length > 0 && (
                <div data-testid={`e360-insights-${fb.id}`} className="mt-3 bg-violet-50/60 border border-violet-200 rounded-md p-3">
                  <div className="text-[11px] tracking-[0.14em] uppercase text-[var(--e360-primary)] mb-1 flex items-center gap-1">
                    <Sparkles size={12} /> Learned from past engagements
                  </div>
                  <ul className="space-y-1">
                    {streamInsights[fb.id].slice(0, 5).map((ins, i) => (
                      <li key={i} className="text-xs ek-text-muted dark:text-[var(--ek-dark-text-muted)]">
                        <span className="ek-text dark:text-[var(--ek-dark-text)] font-medium">{ins.title}</span>
                        {ins.version ? <span className="ek-text-muted dark:text-[var(--ek-dark-text-muted)]"> · {ins.version}</span> : null}
                        {ins.pattern ? <> — {ins.pattern}</> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="text-xs ek-text-muted dark:text-[var(--ek-dark-text-muted)] mt-2">{(fb.risk_control?.controls || []).length} controls · {(fb.kpis?.kpi || []).length} KPIs · {(fb.process_model?.l3?.steps || []).length} L3 steps</div>
              {fb.technology_model?.handoff_note && (
                <p className="text-[12px] ek-text-muted dark:text-[var(--ek-dark-text-muted)] italic mt-2 border-l-2 border-violet-200 pl-2">{fb.technology_model.handoff_note}</p>
              )}
              {fb.process_model?.l3?.lanes?.length > 0 && (
                <div className="mt-3">
                  <div className="text-[11px] tracking-[0.14em] uppercase ek-text-muted dark:text-[var(--ek-dark-text-muted)] mb-1">L3 swim-lane process</div>
                  <div className="space-y-1">
                    {fb.process_model.l3.lanes.map((ln) => (
                      <div key={ln} className="text-xs">
                        <span className="font-medium ek-text dark:text-[var(--ek-dark-text)]">{ln}:</span>{" "}
                        <span className="ek-text-muted dark:text-[var(--ek-dark-text-muted)]">{fb.process_model.l3.steps.filter((s) => s.lane === ln).map((s) => s.name).join(" → ") || "—"}</span>
                      </div>
                    ))}
                  </div>
                  <a data-testid={`e360-bpmn-${fb.id}`} href={`${BASE}/api/admin/enterprise360/value-streams/${fb.id}/map?format=bpmn`}
                    className="inline-block mt-3 mr-2 text-[12px] tracking-[0.14em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-3 py-1.5 rounded">
                    Download editable map (BPMN · Visio/drawio)
                  </a>
                  {onEditMap && (
                    <button data-testid={`e360-editmap-${fb.id}`} onClick={() => onEditMap(fb.id, fb.name)}
                      className="inline-flex items-center gap-1 mt-3 text-[12px] tracking-[0.14em] uppercase text-white bg-[var(--e360-primary)] px-3 py-1.5 rounded">
                      <Pencil size={13} /> Edit map in-app
                    </button>
                  )}
                </div>
              )}
            </details>
          ))}
        </div>
      </div>

      {/* Cross-stream operating-model integration */}
      {bp.operating_model_integration && (bp.operating_model_integration.handoffs || []).length > 0 && (
        <div data-testid="e360-operating-model-integration">
          <Eyebrow>Operating model &amp; interfaces (cross-stream)</Eyebrow>
          <div className="text-xs ek-text-muted dark:text-[var(--ek-dark-text-muted)] mb-2">
            {bp.operating_model_integration.summary?.handoff_count} handoffs ·{" "}
            {bp.operating_model_integration.summary?.shared_role_count} roles accountable across multiple streams
          </div>
          {bp.operating_model_integration.handoff_map_svg && (
            <div className="border ek-line dark:border-[var(--ek-dark-border)] rounded-lg p-3 mb-3 ek-surface dark:bg-[var(--ek-dark-surface)] overflow-x-auto" data-testid="e360-interface-map"
              dangerouslySetInnerHTML={{ __html: bp.operating_model_integration.handoff_map_svg }} />
          )}
          {(bp.operating_model_integration.org_implications || []).length > 0 && (
            <div className="bg-[#0B0B0C] ek-text-muted rounded-lg p-4 mb-3">
              <div className="text-[11px] tracking-[0.18em] uppercase text-[#b89bff] mb-2">Organisation-design implications</div>
              <ul className="space-y-1.5 list-disc pl-4">
                {bp.operating_model_integration.org_implications.map((n, i) => (
                  <li key={i} className="text-xs leading-relaxed">{n}</li>
                ))}
              </ul>
            </div>
          )}
          {(bp.operating_model_integration.shared_accountabilities || []).length > 0 && (
            <div className="mb-3">
              <div className="text-[11px] tracking-[0.14em] uppercase ek-text-muted dark:text-[var(--ek-dark-text-muted)] mb-1">Roles accountable across streams</div>
              <div className="flex flex-wrap gap-2">
                {bp.operating_model_integration.shared_accountabilities.map((s) => (
                  <span key={s.role} title={s.streams.map((x) => x.replace(/_/g, " ")).join(", ")}
                    className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-3 py-1">
                    {s.role} · {s.streams.length} streams
                  </span>
                ))}
              </div>
            </div>
          )}
          <details className="border ek-line dark:border-[var(--ek-dark-border)] rounded-lg p-3" data-testid="e360-handoff-register">
            <summary className="cursor-pointer text-sm font-medium ek-text dark:text-[var(--ek-dark-text)]">Handoff &amp; interface register ({bp.operating_model_integration.handoffs.length})</summary>
            <div className="mt-2 divide-y">
              {bp.operating_model_integration.handoffs.map((h, i) => (
                <div key={i} className="py-1.5 text-xs">
                  <span className="font-medium ek-text dark:text-[var(--ek-dark-text)]">{h.from}</span>
                  <span className="text-[var(--e360-primary)] mx-1">→</span>
                  <span className="font-medium ek-text dark:text-[var(--ek-dark-text)]">{h.to}</span>
                  <span className="ek-text-muted dark:text-[var(--ek-dark-text-muted)]"> — {h.why}</span>
                </div>
              ))}
            </div>
          </details>
        </div>
      )}

      <ReportSections rs={bp.report_sections} />
    </div>
  );
}

const RAG = ({ status }) => {
  const map = {
    red: "text-red-700 bg-red-50 border-red-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
    green: "text-emerald-700 bg-emerald-50 border-emerald-200",
  };
  const lbl = { red: "Red", amber: "Amber", green: "Green" }[status] || "Green";
  return <span className={`text-[11px] rounded-full px-2 py-0.5 border font-semibold ${map[status] || map.green}`}>● {lbl}</span>;
};

function ReportSections({ rs }) {
  if (!rs) return null;
  const h = rs.programme_health || {};
  const gaps = rs.gap_analysis || [];
  const ben = rs.benefits || {};
  const ch = rs.change_adoption || {};
  const raid = rs.raid || {};
  const conc = (ch.concentration && ch.concentration.hits) || [];
  return (
    <div className="space-y-6 pt-2 border-t ek-line dark:border-[var(--ek-dark-border)]" data-testid="e360-report-sections">
      <p className="text-[11px] ek-text-muted dark:text-[var(--ek-dark-text-muted)] -mb-2">The board-grade report sections below render from the same data as the downloadable Reinvention Kit — what you see here is what the client gets.</p>

      {/* Programme health RAG */}
      {(h.signals || []).length > 0 && (
        <div data-testid="e360-programme-health">
          <Eyebrow>Programme health at a glance</Eyebrow>
          <div className="text-sm ek-text-muted dark:text-[var(--ek-dark-text-muted)] mb-2 flex items-center gap-2">
            Overall readiness: <RAG status={h.overall} />
            <span className="text-[11px] ek-text-muted dark:text-[var(--ek-dark-text-muted)]">({h.reds} red · {h.ambers} amber · {h.greens} green)</span>
          </div>
          <div className="border ek-line dark:border-[var(--ek-dark-border)] rounded-lg divide-y">
            {h.signals.map((s, i) => (
              <div key={i} className="px-3 py-2" data-testid={`e360-health-signal-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium ek-text dark:text-[var(--ek-dark-text)]">{s.signal}</span>
                  <RAG status={s.status} />
                </div>
                <div className="text-xs ek-text-muted dark:text-[var(--ek-dark-text-muted)] mt-1">{s.found}</div>
                <div className="text-[11px] ek-text-muted dark:text-[var(--ek-dark-text-muted)] mt-0.5">Action: {s.action} · see {s.detail_label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Auto shared-service / PMO recommendation */}
      {conc.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="e360-concentration-reco">
          <div className="text-[12px] tracking-[0.14em] uppercase text-amber-800 dark:text-amber-300 font-semibold">Structural recommendation — auto-flagged</div>
          <ul className="mt-2 space-y-1">
            {conc.map((c, i) => (
              <li key={i} className="text-[12px] text-amber-900 dark:text-amber-200">
                <b>{c.role}</b> is {c.kind === "accountable" ? "accountable owner for" : "coordinating across"} <b>{c.streams.length}</b> value streams.
              </li>
            ))}
          </ul>
          <p className="text-[12px] text-amber-900/90 dark:text-amber-200 mt-2">Delivery-bottleneck risk — recommend shared-service / PMO support: delegated decision rights, a PMO to run cadence and unblock, and named deputies per stream, resourced at the 30-day horizon.</p>
        </div>
      )}

      {/* Gap analysis */}
      {gaps.length > 0 && (
        <details className="border ek-line dark:border-[var(--ek-dark-border)] rounded-lg p-3" data-testid="e360-gap-analysis">
          <summary className="cursor-pointer font-medium ek-text dark:text-[var(--ek-dark-text)]">Gap analysis (current → target) · {gaps.length} streams</summary>
          <div className="mt-2 divide-y">
            {gaps.map((g, i) => (
              <div key={i} className="py-2 text-xs flex items-center justify-between gap-3">
                <span className="ek-text dark:text-[var(--ek-dark-text)] font-medium flex-1">{g.name}</span>
                <span className="ek-text-muted dark:text-[var(--ek-dark-text-muted)] whitespace-nowrap">{g.current_index != null ? `${g.current_index}` : "—"} → {g.target_index} <span className="text-[var(--e360-primary)] font-semibold">(+{g.gap ?? "—"})</span></span>
                <span className="ek-text-muted dark:text-[var(--ek-dark-text-muted)] whitespace-nowrap">{g.owner}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Benefits realisation */}
      {(ben.rows || []).length > 0 && (
        <details className="border ek-line dark:border-[var(--ek-dark-border)] rounded-lg p-3" data-testid="e360-benefits">
          <summary className="cursor-pointer font-medium ek-text dark:text-[var(--ek-dark-text)]">Benefits realisation · {ben.rows.length} benefits{ben.value_at_stake_label ? ` · ~${ben.value_at_stake_label} at stake` : ""}</summary>
          <div className="mt-2 divide-y">
            {ben.rows.map((b, i) => (
              <div key={i} className="py-2 text-xs">
                <div className="ek-text dark:text-[var(--ek-dark-text)] font-medium">{b.benefit}</div>
                <div className="ek-text-muted dark:text-[var(--ek-dark-text-muted)]">Owner: {b.owner} · Baseline: {b.baseline} · Target: {b.target}</div>
                <div className="ek-text-muted dark:text-[var(--ek-dark-text-muted)]">Measure: {b.measure} · {b.cadence}</div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Change & adoption */}
      {(ch.stakeholders || []).length > 0 && (
        <details className="border ek-line dark:border-[var(--ek-dark-border)] rounded-lg p-3" data-testid="e360-change-adoption">
          <summary className="cursor-pointer font-medium ek-text dark:text-[var(--ek-dark-text)]">Change &amp; adoption · {ch.stakeholders.length} stakeholders</summary>
          <div className="mt-3">
            <div className="text-[11px] tracking-[0.14em] uppercase ek-text-muted dark:text-[var(--ek-dark-text-muted)] mb-1">Top stakeholders</div>
            <div className="flex flex-wrap gap-1.5">
              {ch.stakeholders.slice(0, 12).map((s, i) => (
                <span key={i} className="text-[11px] ek-surface-2 dark:bg-[var(--ek-dark-surface-hi)] ek-text-soft dark:text-[var(--ek-dark-text)] rounded-full px-2.5 py-0.5" title={s.approach}>
                  {s.role} · {s.streams} · {s.influence}
                </span>
              ))}
              {ch.stakeholders.length > 12 && <span className="text-[11px] ek-text-muted dark:text-[var(--ek-dark-text-muted)]">+{ch.stakeholders.length - 12} more</span>}
            </div>
            {(ch.adoption_metrics || []).length > 0 && (
              <>
                <div className="text-[11px] tracking-[0.14em] uppercase ek-text-muted dark:text-[var(--ek-dark-text-muted)] mt-3 mb-1">Adoption metrics</div>
                <ul className="space-y-1">
                  {ch.adoption_metrics.map((m, i) => (
                    <li key={i} className="text-xs ek-text-muted dark:text-[var(--ek-dark-text-muted)]"><span className="ek-text dark:text-[var(--ek-dark-text)] font-medium">{m.metric}</span> — {m.target} <span className="ek-text-muted dark:text-[var(--ek-dark-text-muted)]">({m.cadence})</span></li>
                  ))}
                </ul>
              </>
            )}
            {(ch.resistance || []).length > 0 && (
              <>
                <div className="text-[11px] tracking-[0.14em] uppercase ek-text-muted dark:text-[var(--ek-dark-text-muted)] mt-3 mb-1">Resistance management</div>
                <ul className="space-y-1">
                  {ch.resistance.map((r, i) => (
                    <li key={i} className="text-xs ek-text-muted dark:text-[var(--ek-dark-text-muted)]"><span className="ek-text dark:text-[var(--ek-dark-text)] font-medium">{r.source}</span> → {r.mitigation}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </details>
      )}

      {/* RAID log */}
      {(raid.entries || []).length > 0 && (
        <details className="border ek-line dark:border-[var(--ek-dark-border)] rounded-lg p-3" data-testid="e360-raid-log">
          <summary className="cursor-pointer font-medium ek-text dark:text-[var(--ek-dark-text)]">
            RAID &amp; dependency log · {Object.entries(raid.counts || {}).map(([k, v]) => `${v} ${k}${v === 1 ? "" : "s"}`).join(" · ")}
          </summary>
          <div className="mt-2 divide-y">
            {raid.entries.slice(0, 25).map((e, i) => (
              <div key={i} className="py-1.5 text-xs flex items-start gap-2">
                <span className="ek-text-muted dark:text-[var(--ek-dark-text-muted)] whitespace-nowrap w-16">{e.ref}</span>
                <span className={`whitespace-nowrap w-20 ${e.category === "Risk" ? "text-red-600" : e.category === "Issue" ? "text-amber-600" : "ek-text-muted dark:text-[var(--ek-dark-text-muted)]"}`}>{e.category}</span>
                <span className="ek-text-soft dark:text-[var(--ek-dark-text)] flex-1">{e.item}</span>
                <span className="ek-text-muted dark:text-[var(--ek-dark-text-muted)] whitespace-nowrap">{e.owner}</span>
              </div>
            ))}
            {raid.entries.length > 25 && <div className="py-1.5 text-[11px] ek-text-muted dark:text-[var(--ek-dark-text-muted)]">+{raid.entries.length - 25} more in the full Kit report.</div>}
          </div>
        </details>
      )}
    </div>
  );
}
